import React from 'react';
import { useParams } from 'react-router-dom';

import withLazyLoading from '../../hoc/withLazyLoading';
import { CTL_CONFIG_PATTERNS, CTL_CONFIG_ROUTES, USER_CENTER_ROUTES } from '../../navigation/paths';
import { createCrumbHandle, withCrumbParents } from '../../utilities/auth';
import type { CrumbParent } from '../../utilities/auth';
import { EQUIPMENT_LIBRARY_RULES, access, param } from '../../access';

import { guardedRoute } from './guardedRoute';

const FEATURE = 'ctl-config';

const LibraryListScreen = withLazyLoading(() =>
  import('../../modules/ctl-config/components/equipment-library/LibraryListScreen').then((m) => ({ default: m.LibraryListScreen })),
);

const PlantTypesScreen = withLazyLoading(() =>
  import('../../modules/ctl-config/components/dictionaries/PlantTypesScreen').then((m) => ({ default: m.PlantTypesScreen })),
);

const DatabaseScreen = withLazyLoading(() =>
  import('../../modules/ctl-config/components/dictionaries/DatabaseScreen').then((m) => ({ default: m.DatabaseScreen })),
);

const TypeDetailScreen = withLazyLoading(() =>
  import('../../modules/ctl-config/components/equipment-library/TypeDetailScreen').then((m) => ({ default: m.TypeDetailScreen })),
);

const TypeEditRoute = withLazyLoading(() =>
  import('../../modules/ctl-config/components/equipment-library/TypeEditRoute').then((m) => ({ default: m.TypeEditRoute })),
);

const ReadScadaScreen = withLazyLoading(() =>
  import('../../modules/ctl-config/components/scada/ReadScadaScreen').then((m) => ({ default: m.ReadScadaScreen })),
);

const MachineListScreen = withLazyLoading(() =>
  import('../../modules/ctl-config/components/machines/MachineListScreen').then((m) => ({ default: m.MachineListScreen })),
);

const MachineCardScreen = withLazyLoading(() =>
  import('../../modules/ctl-config/components/machines/MachineCardScreen').then((m) => ({ default: m.MachineCardScreen })),
);

const GuardedTypeDetail: React.FC = () => {
  const { code = '' } = useParams();
  return guardedRoute(FEATURE, <TypeDetailScreen key={code} code={code} />, [code]);
};

const GuardedTypeEdit: React.FC = () => {
  const { code = '' } = useParams();
  return guardedRoute(FEATURE, <TypeEditRoute key={code} code={code} />, [code]);
};

const GuardedReadScada: React.FC = () => {
  const { plantId = '' } = useParams();
  return guardedRoute(FEATURE, <ReadScadaScreen key={plantId} plantId={plantId} />, [plantId]);
};

const GuardedMachineList: React.FC = () => {
  const { plantId = '' } = useParams();
  return guardedRoute(FEATURE, <MachineListScreen key={plantId} plantId={plantId} />, [plantId]);
};

const GuardedMachineCard: React.FC = () => {
  const { plantId = '', entryId = '' } = useParams();
  const identity = `${plantId}/${entryId}`;
  return guardedRoute(FEATURE, <MachineCardScreen key={identity} plantId={plantId} entryId={entryId} />, [plantId, entryId]);
};

const LIBRARY_CRUMB: CrumbParent = { label: 'Equipment library', pathname: CTL_CONFIG_ROUTES.library };
const PLANTS_CRUMB: CrumbParent = { label: 'Plants & modules', pathname: USER_CENTER_ROUTES.plants };
const TYPE_CRUMB: CrumbParent = { label: 'Equipment type', pathname: ({ code }) => CTL_CONFIG_ROUTES.type(code ?? '') };
const MACHINES_CRUMB: CrumbParent = { label: 'Machines', pathname: ({ plantId }) => CTL_CONFIG_ROUTES.machines(plantId ?? '') };

const ctlConfigRoutes = [
  {
    path: CTL_CONFIG_PATTERNS.library,
    element: guardedRoute(FEATURE, <LibraryListScreen />),
    handle: { ...createCrumbHandle('Equipment library'), ...access(EQUIPMENT_LIBRARY_RULES.view) },
  },
  {
    path: CTL_CONFIG_PATTERNS.plantTypes,
    element: guardedRoute(FEATURE, <PlantTypesScreen />),
    handle: { ...withCrumbParents(createCrumbHandle('Plant types'), [LIBRARY_CRUMB]), ...access(EQUIPMENT_LIBRARY_RULES.view) },
  },
  {
    path: CTL_CONFIG_PATTERNS.database,
    element: guardedRoute(FEATURE, <DatabaseScreen />),
    handle: { ...withCrumbParents(createCrumbHandle('Database'), [LIBRARY_CRUMB]), ...access(EQUIPMENT_LIBRARY_RULES.view) },
  },
  {
    path: CTL_CONFIG_PATTERNS.type,
    element: <GuardedTypeDetail />,
    handle: { ...withCrumbParents(createCrumbHandle('Equipment type'), [LIBRARY_CRUMB]), ...access(EQUIPMENT_LIBRARY_RULES.view) },
  },
  {
    path: CTL_CONFIG_PATTERNS.typeEdit,
    element: <GuardedTypeEdit />,
    handle: { ...withCrumbParents(createCrumbHandle('Edit equipment type'), [LIBRARY_CRUMB, TYPE_CRUMB]), ...access(EQUIPMENT_LIBRARY_RULES.manage) },
  },
  {
    path: CTL_CONFIG_PATTERNS.scada,
    element: <GuardedReadScada />,
    handle: {
      ...withCrumbParents(createCrumbHandle('What SCADA found'), [PLANTS_CRUMB]),
      ...access(EQUIPMENT_LIBRARY_RULES.plant, param('plantId')),
    },
  },
  {
    path: CTL_CONFIG_PATTERNS.machines,
    element: <GuardedMachineList />,
    handle: { ...withCrumbParents(createCrumbHandle('Machines'), [PLANTS_CRUMB]), ...access(EQUIPMENT_LIBRARY_RULES.plant, param('plantId')) },
  },
  {
    path: CTL_CONFIG_PATTERNS.machine,
    element: <GuardedMachineCard />,
    handle: { ...withCrumbParents(createCrumbHandle('Machine'), [PLANTS_CRUMB, MACHINES_CRUMB]), ...access(EQUIPMENT_LIBRARY_RULES.plant, param('plantId')) },
  },
];

export default ctlConfigRoutes;
